/**
 * WriteConfirmationDialog - Confirmation modal for AI write operations
 *
 * Displays pending action details with changes preview.
 * All write operations require explicit user confirmation.
 */

import { cn } from "@plasma/utils/cn";
import {
  AlertTriangle,
  CalendarClock,
  Check,
  ClipboardList,
  Clock,
  DollarSign,
  Package,
  Percent,
  ShoppingBag,
  Users,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Spinner } from "~/components/ui/spinner";
import { logger, PortalEvents } from "~/lib/logger";

import type {
  ChangePreview,
  PendingAction,
} from "./hooks/use-write-confirmation";

interface WriteConfirmationDialogProps {
  /** The pending action to confirm */
  pendingAction: PendingAction | null;
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Close the dialog */
  onClose: () => void;
  /** Confirm the action */
  onConfirm: () => Promise<void>;
  /** Reject the action */
  onReject: () => Promise<void>;
  /** Whether an action is being processed */
  isProcessing: boolean;
}

/** Get icon for entity type */
function getEntityIcon(type: string) {
  switch (type) {
    case "item":
      return Package;
    case "price":
      return DollarSign;
    case "discount":
      return Percent;
    case "employee":
      return Users;
    case "order":
      return ShoppingBag;
    case "menuItem":
      return ClipboardList;
    case "businessHours":
      return CalendarClock;
    default:
      return AlertTriangle;
  }
}

/** Format a value for display */
function formatValue(value: string | number | boolean | null): string {
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "number") {
    // Check if it looks like a price (has decimal or is reasonable currency amount)
    if (value > 0 && value < 100_000) {
      return `$${value.toFixed(2)}`;
    }
    return value.toString();
  }
  return value;
}

/** Single change row */
function ChangeRow({ change }: { change: ChangePreview }) {
  const fromValue = formatValue(change.from);
  const toValue = formatValue(change.to);

  return (
    <div className="border-border/50 flex items-center justify-between border-b py-2 last:border-0">
      <span className="text-muted-foreground text-sm capitalize">
        {change.field.replace(/([A-Z])/g, " $1").trim()}
      </span>
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground line-through">{fromValue}</span>
        <span className="text-muted-foreground">→</span>
        <span className="text-foreground font-medium">{toValue}</span>
      </div>
    </div>
  );
}

/** Countdown timer display */
function ExpiryTimer({ expiresAt }: { expiresAt: number }) {
  const [timeLeft, setTimeLeft] = useState(() =>
    Math.max(0, Math.floor((expiresAt - Date.now()) / 1000))
  );

  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = Math.max(
        0,
        Math.floor((expiresAt - Date.now()) / 1000)
      );
      setTimeLeft(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  const isLow = timeLeft < 60;

  return (
    <div
      className={cn(
        "flex items-center gap-1 text-xs",
        isLow ? "text-destructive" : "text-muted-foreground"
      )}
    >
      <Clock className="h-3 w-3" />
      <span>
        Expires in {minutes}:{seconds.toString().padStart(2, "0")}
      </span>
    </div>
  );
}

export function WriteConfirmationDialog({
  pendingAction,
  isOpen,
  onClose,
  onConfirm,
  onReject,
  isProcessing,
}: WriteConfirmationDialogProps) {
  if (!pendingAction) {
    return null;
  }

  const EntityIcon = getEntityIcon(pendingAction.affectedEntity.type);

  const handleConfirm = async () => {
    try {
      logger.info(PortalEvents.AI_WRITE_CONFIRMED, {
        action_type: pendingAction.affectedEntity.type,
        entity_name: pendingAction.affectedEntity.name,
        changes_count: pendingAction.changes.length,
      });
      await onConfirm();
    } catch {
      // Error handled in hook
    }
  };

  const handleReject = async () => {
    try {
      logger.info(PortalEvents.AI_WRITE_REJECTED, {
        action_type: pendingAction.affectedEntity.type,
        entity_name: pendingAction.affectedEntity.name,
      });
      await onReject();
    } catch {
      // Error handled in hook
    }
  };

  return (
    <AlertDialog onOpenChange={(open) => !open && onClose()} open={isOpen}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center justify-between">
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-warning h-5 w-5" />
              Confirm Action
            </AlertDialogTitle>
            <ExpiryTimer expiresAt={pendingAction.expiresAt} />
          </div>
          <AlertDialogDescription className="text-left">
            {pendingAction.description}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Affected Entity */}
        <div className="bg-muted/30 rounded-lg border p-3">
          <div className="mb-2 flex items-center gap-2">
            <EntityIcon className="text-muted-foreground h-4 w-4" />
            <span className="text-sm font-medium">
              {pendingAction.affectedEntity.name}
            </span>
            <span className="text-muted-foreground text-xs capitalize">
              ({pendingAction.affectedEntity.type})
            </span>
          </div>

          {/* Changes Preview */}
          {pendingAction.changes.length > 0 && (
            <div className="mt-2">
              {pendingAction.changes.map((change) => (
                <ChangeRow change={change} key={change.field} />
              ))}
            </div>
          )}
        </div>

        <AlertDialogFooter className="gap-2 sm:gap-2">
          <Button
            className="flex-1"
            disabled={isProcessing}
            onClick={handleReject}
            type="button"
            variant="outline"
          >
            {isProcessing ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <>
                <X className="h-4 w-4" />
                Reject
              </>
            )}
          </Button>
          <Button
            className="flex-1"
            disabled={isProcessing}
            onClick={handleConfirm}
            type="button"
            variant="default"
          >
            {isProcessing ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <>
                <Check className="h-4 w-4" />
                Confirm
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
